import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react'
import { STORAGE_KEYS } from '@/utils/storageKeys'
import { useSearchParams } from 'react-router'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import ptBrLocale from '@fullcalendar/core/locales/pt-br'
import type { DatesSetArg, EventClickArg } from '@fullcalendar/core'
import { Box, Button, Card, Flex, Spinner, useBreakpointValue } from '@chakra-ui/react'
import { LuBell, LuBellRing, LuCalendarPlus, LuLayoutGrid, LuLayoutList } from 'react-icons/lu'
import { format, startOfMonth, endOfMonth } from 'date-fns'

import { useCalendar } from '@/hooks/calendar/useCalendar'
import { buildCalendarParams } from '@/hooks/calendar/useCalendarParams'
import { useEventModals } from '@/hooks/calendar/useEventModals'
import { STATUS_COLORS, DEFAULT_EVENT_COLOR, HOLIDAY_COLOR, REMINDER_COLOR } from '../constants'
import Filters, { type CalendarFilterState } from './Filters'
import MobileAgenda from './MobileAgenda'
import CalendarModals from './CalendarModals'
import Tooltip from '@/components/Tooltip'
import './calendar.css'

// ─── Pure helpers ────────────────────────────────────────────────────────────

const formatMonthTitle = (arg: { date: { marker: Date } }) => {
  const month = new Intl.DateTimeFormat('pt-BR', { month: 'long', timeZone: 'UTC' }).format(arg.date.marker)
  const year = arg.date.marker.getUTCFullYear()
  return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`
}

const formatDayTitle = (arg: { date: { marker: Date } }) => {
  const date = arg.date.marker
  const day = date.getUTCDate()
  const month = new Intl.DateTimeFormat('pt-BR', { month: 'long', timeZone: 'UTC' }).format(date)
  const year = date.getUTCFullYear()
  return `${day} de ${month} de ${year}`
}

// ─── Event chip ──────────────────────────────────────────────────────────────

type EventChipProps = {
  title: string
  timeText: string
  isReminder: boolean
  isToday: boolean
}

const EventChip = ({ title, timeText, isReminder, isToday }: EventChipProps) => {
  const textRef = useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const BellIcon = isToday ? LuBellRing : LuBell
  const fullLabel = `${!isReminder && timeText ? `${timeText}h ` : ''}${title}`

  useLayoutEffect(() => {
    const el = textRef.current
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth)
  }, [fullLabel])

  return (
    <Tooltip content={title} disabled={!isTruncated} openDelay={400}>
      <Box px="3px" overflow="hidden">
        <Flex align="center" gap="2px" overflow="hidden">
          {isReminder && <Box flexShrink={0}><BellIcon size={10} color="white" /></Box>}
          <Box
            ref={textRef}
            fontSize="xs"
            fontWeight="600"
            color="white"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {fullLabel}
          </Box>
        </Flex>
      </Box>
    </Tooltip>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

type MobileViewToggleProps = {
  view: 'grid' | 'agenda'
  onChange: (view: 'grid' | 'agenda') => void
}

const MobileViewToggle = ({ view, onChange }: MobileViewToggleProps) => (
  <Flex flex="1">
    <Flex
      w="full"
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
      p="3px"
      gap="2px"
      h="9"
      align="center"
    >
      <Flex
        as="button"
        flex="1"
        justify="center"
        align="center"
        gap={1}
        px={3}
        h="full"
        borderRadius="md"
        bg={view === 'grid' ? 'primary.700' : 'transparent'}
        color={view === 'grid' ? 'white' : 'gray.400'}
        fontWeight="medium"
        fontSize="xs"
        transition="all 0.15s"
        onClick={() => onChange('grid')}
      >
        <LuLayoutGrid size={13} />
        Calendário
      </Flex>
      <Flex
        as="button"
        flex="1"
        justify="center"
        align="center"
        gap={1}
        px={3}
        h="full"
        borderRadius="md"
        bg={view === 'agenda' ? 'primary.700' : 'transparent'}
        color={view === 'agenda' ? 'white' : 'gray.400'}
        fontWeight="medium"
        fontSize="xs"
        transition="all 0.15s"
        onClick={() => onChange('agenda')}
      >
        <LuLayoutList size={13} />
        Agenda
      </Flex>
    </Flex>
  </Flex>
)

type NewEventButtonProps = {
  onClick: () => void
  iconOnly?: boolean
}

const NewEventButton = ({ onClick, iconOnly }: NewEventButtonProps) => (
  <Button size="sm" colorPalette="primary" onClick={onClick} aria-label="Novo evento">
    <LuCalendarPlus />
    {!iconOnly && 'Novo evento'}
  </Button>
)

// ─── Main component ───────────────────────────────────────────────────────────

const Calendar = () => {
  const [searchParams] = useSearchParams()

  const [dateRange, setDateRange] = useState(() => {
    const now = new Date()
    return {
      start: format(startOfMonth(now), 'yyyy-MM-dd'),
      end: format(endOfMonth(now), 'yyyy-MM-dd'),
    }
  })

  const [filters, setFilters] = useState<CalendarFilterState>(() => {
    const agendaId = searchParams.get('agendaId') ?? undefined
    return { search: '', agendaId }
  })

  const [mobileView, setMobileView] = useState<'grid' | 'agenda'>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.CALENDAR_MOBILE_VIEW)
    return stored === 'agenda' ? 'agenda' : 'grid'
  })

  const { selectedEvent, newEventOpen, setNewEventOpen, openEvent, closeEvent } = useEventModals()

  const isMobile = useBreakpointValue({ base: true, md: false })

  const [calendarKey, setCalendarKey] = useState(0)
  const [isResizing, setIsResizing] = useState(false)

  useEffect(() => {
    const handler = () => {
      setIsResizing(true)
      setTimeout(() => {
        setCalendarKey((k) => k + 1)
        setIsResizing(false)
      }, 250)
    }
    window.addEventListener('sidebar-toggle', handler)
    return () => window.removeEventListener('sidebar-toggle', handler)
  }, [])

  const calendarParams = buildCalendarParams(filters, dateRange)
  const { data, isFetching } = useCalendar(calendarParams)

  const calendarEvents = useMemo(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd')

    const events = (data?.events ?? []).map((event) => {
      const agenda = event.agenda as unknown as { id: string; name: string } | string | null
      const agendaId = typeof agenda === 'object' && agenda !== null ? agenda.id : null
      return {
        id: event.id,
        title: event.name,
        start: event.event_time ? `${event.event_date}T${event.event_time}` : event.event_date,
        allDay: !event.event_time,
        backgroundColor: STATUS_COLORS[event.status] ?? DEFAULT_EVENT_COLOR,
        borderColor: STATUS_COLORS[event.status] ?? DEFAULT_EVENT_COLOR,
        textColor: '#ffffff',
        extendedProps: { agendaId },
      }
    })

    const holidays = (data?.holidays ?? []).map((holiday) => ({
      id: `holiday-${holiday.date}-${holiday.name}`,
      title: holiday.name,
      start: holiday.date,
      allDay: true,
      backgroundColor: HOLIDAY_COLOR,
      borderColor: HOLIDAY_COLOR,
      textColor: '#ffffff',
      extendedProps: {},
    }))

    const reminders = (data?.reminders ?? []).map((reminder) => ({
      id: `reminder-${reminder.id}`,
      title: reminder.note ?? 'Lembrete',
      start: reminder.reminder_date,
      allDay: true,
      backgroundColor: REMINDER_COLOR,
      borderColor: REMINDER_COLOR,
      textColor: '#ffffff',
      extendedProps: {
        agendaId: reminder.agenda_id,
        eventId: reminder.event_id,
        type: 'reminder',
        isToday: reminder.reminder_date === todayStr,
      },
    }))

    return [...events, ...holidays, ...reminders]
  }, [data])

  const handleEventClick = (arg: EventClickArg) => {
    const agendaId = arg.event.extendedProps.agendaId as string | null
    const eventId = (arg.event.extendedProps.eventId as string | null) ?? arg.event.id
    if (agendaId) {
      openEvent(agendaId, eventId)
      const closeBtn = document.querySelector('.fc-popover-close') as HTMLElement | null
      closeBtn?.click()
    }
  }

  const handleDatesSet = (arg: DatesSetArg) => {
    setDateRange({
      start: format(arg.start, 'yyyy-MM-dd'),
      end: format(arg.end, 'yyyy-MM-dd'),
    })
  }

  const handleMobileViewChange = (view: 'grid' | 'agenda') => {
    setMobileView(view)
    localStorage.setItem(STORAGE_KEYS.CALENDAR_MOBILE_VIEW, view)
  }

  const filterBar = (
    <Filters
      agendas={data?.agendas ?? []}
      value={filters}
      onChange={setFilters}
      isMobile={!!isMobile}
    />
  )

  const mobileHeader = (
    <Box mb={5}>
      <Flex align="center" gap={3} mb={4}>
        <MobileViewToggle view={mobileView} onChange={handleMobileViewChange} />
        <NewEventButton onClick={() => setNewEventOpen(true)} iconOnly />
      </Flex>
      {filterBar}
    </Box>
  )

  if (isMobile && mobileView === 'agenda') {
    return (
      <Box w="full">
        {mobileHeader}
        <MobileAgenda filters={filters} />
        <CalendarModals
          selectedEvent={selectedEvent}
          newEventOpen={newEventOpen}
          agendas={data?.agendas ?? []}
          onCloseEvent={closeEvent}
          onCloseNew={setNewEventOpen}
        />
      </Box>
    )
  }

  return (
    <Box w="full" display="flex" flexDirection="column" flex="1">
      {!isMobile && (
        <Flex gap={3} mb={3} align="flex-start">
          {filterBar}
          <NewEventButton onClick={() => setNewEventOpen(true)} />
        </Flex>
      )}

      <Card.Root
        w="full"
        flex="1"
        position="relative"
        overflow={isMobile ? 'visible' : 'hidden'}
        bg={isMobile ? 'transparent' : undefined}
        border={isMobile ? 'none' : undefined}
        boxShadow={isMobile ? 'none' : undefined}
      >
        <Card.Body p={isMobile ? 0 : 4} display="flex" flexDirection="column">
          {isFetching && (
            <Flex position="absolute" top="48px" left="0" right="0" justify="center" zIndex={10}>
              <Spinner size="sm" color="primary.500" />
            </Flex>
          )}

          {isMobile && mobileHeader}

          <Box flex="1" position="relative">
            {isResizing && (
              <Flex
                position="absolute"
                inset="0"
                justify="center"
                align="center"
                bg="whiteAlpha.700"
                zIndex={5}
                borderRadius="md"
              >
                <Spinner size="md" color="primary.500" />
              </Flex>
            )}
            <FullCalendar
              key={calendarKey}
              plugins={[dayGridPlugin]}
              initialView="dayGridMonth"
              locale={ptBrLocale}
              headerToolbar={{
                left: 'prev,today,next',
                center: 'title',
                right: 'dayGridMonth,dayGridWeek,dayGridDay',
              }}
              titleFormat={formatMonthTitle}
              height={isMobile ? 'auto' : 'calc(100vh - 128px)'}
              firstDay={1}
              navLinks
              fixedWeekCount={false}
              expandRows={!isMobile}
              events={calendarEvents}
              datesSet={handleDatesSet}
              eventClick={handleEventClick}
              dayMaxEvents={isMobile ? 2 : 3}
              views={{
                dayGridDay: {
                  dayMaxEvents: false,
                  titleFormat: formatDayTitle,
                },
              }}
              eventDisplay="block"
              eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
              eventContent={(arg) => (
                <EventChip
                  title={arg.event.title}
                  timeText={arg.timeText}
                  isReminder={arg.event.extendedProps.type === 'reminder'}
                  isToday={arg.event.extendedProps.isToday}
                />
              )}
            />
          </Box>
        </Card.Body>

        <CalendarModals
          selectedEvent={selectedEvent}
          newEventOpen={newEventOpen}
          agendas={data?.agendas ?? []}
          onCloseEvent={closeEvent}
          onCloseNew={setNewEventOpen}
        />
      </Card.Root>
    </Box>
  )
}

export default Calendar
